import '../styles/ImplementationSteps.scss';

import { Card } from '@whistler-ui/k2/components/Card';
import { TimelineNavigation } from '@whistler-ui/k2/components/TimelineNavigation';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { useCallback, useMemo } from 'react';

import { useTimeline } from '../../../hooks';
import { BrandResponse } from '../../../types';
import { ImplementationStepsPropsType } from '../types';
import { ImplementationStepsSkeleton } from './ImplementationStepsSkeleton';

const componentName = 'c-ImplementationSteps';

interface Props extends ImplementationStepsPropsType<BrandResponse> {
  loading?: boolean;
  settings: any;
  stepsDisabled?: boolean;
}

export const ImplementationSteps = ({
  brand,
  callbacks,
  tag,
  pages,
  loading,
  settings,
  stepsDisabled,
}: Props) => {
  const c = useClassHelper(componentName);

  const visiblePages = useMemo(() => {
    return pages.filter(page => !page.hidden?.(settings));
  }, [pages, settings]);

  const {
    goForward,
    goBackward,
    goTo,
    currentPage,
    currentPageIndex,
    validKeys,
  } = useTimeline(visiblePages);

  const CurrentPageComponent = useMemo(() => {
    if (currentPage?.component) {
      return currentPage.component;
    }

    return () => null;
  }, [currentPage?.key]);

  const handleGoForward = useCallback(() => {
    if (currentPageIndex < validKeys.length - 1) {
      goForward();
    }

    if (currentPageIndex === validKeys.length - 1) {
      callbacks?.goForwardSubNavigation();
    }
  }, [currentPageIndex, validKeys.length, goForward, callbacks]);

  return (
    <Card
      classNames={{
        container: componentName,
        content: 'flex flex-col gap-10',
      }}
    >
      <div className={c('__timeline-container')}>
        <TimelineNavigation
          variant="above"
          currentPageIndex={currentPageIndex}
          totalPages={validKeys?.length}
          currentPage={currentPage?.key}
          goTo={goTo}
          pages={validKeys}
          c={c}
          loading={loading}
          disabledPages={stepsDisabled ? validKeys.slice(1) : []}
        />
      </div>
      {loading ? (
        <ImplementationStepsSkeleton />
      ) : (
        <CurrentPageComponent
          data={settings}
          currentPageIndex={currentPageIndex}
          tag={tag}
          brand={brand}
          brandId={brand.id}
          environmentId={brand.environment_id}
          loading={loading}
          callbacks={{
            goForward: handleGoForward,
            goForwardSubNavigation: callbacks?.goForwardSubNavigation,
            goBackward: goBackward,
          }}
        />
      )}
    </Card>
  );
};
